import { useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowLeft, Bell, Calendar, CreditCard, Gift, Info } from 'lucide-react'
import { clsx } from 'clsx'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { usePatientAuth } from '@hooks/usePatientAuth'
import { notificationService, type Notification } from '@lib/supabase/notification.service'
import { queryKeys } from '@lib/queryKeys'

function formatTime(dateString: string) {
  const date = new Date(dateString)
  const now = new Date()
  const diff = now.getTime() - date.getTime()
  const seconds = Math.floor(diff / 1000)
  const minutes = Math.floor(seconds / 60)
  const hours = Math.floor(minutes / 60)
  const days = Math.floor(hours / 24)

  if (seconds < 60) return 'Just now'
  if (minutes < 60) return `${minutes}m ago`
  if (hours < 24) return `${hours}h ago`
  if (days < 7) return `${days}d ago`
  return date.toLocaleDateString()
}

function NotificationIcon({ type }: { type: Notification['type'] }) {
  switch (type) {
    case 'appointment':
      return <Calendar size={18} className="text-sky-600" />
    case 'payment':
    case 'earning':
    case 'withdrawal':
      return <CreditCard size={18} className="text-emerald-600" />
    case 'promotion':
      return <Gift size={18} className="text-violet-600" />
    default:
      return <Info size={18} className="text-muted-foreground" />
  }
}

export default function NotificationsPage() {
  const navigate = useNavigate()
  const { patient } = usePatientAuth()
  const queryClient = useQueryClient()
  const notificationsQueryKey = queryKeys.patient.notifications(patient?.id)
  const unreadNotificationsQueryKey = queryKeys.patient.unreadNotifications(patient?.id)

  const notificationsQuery = useQuery({
    queryKey: notificationsQueryKey,
    queryFn: () => notificationService.getPatientNotifications(patient!.id),
    enabled: !!patient?.id,
  })

  const notifications = notificationsQuery.data ?? []
  const isLoading = notificationsQuery.isPending

  const markAllAsReadMutation = useMutation({
    mutationFn: () => notificationService.markAllAsReadForPatient(patient!.id),
    onSuccess: (result) => {
      if (!result.success) return

      queryClient.setQueryData<Notification[]>(notificationsQueryKey, (current = []) =>
        current.map((item) => ({ ...item, read: true }))
      )
      queryClient.setQueryData<number>(unreadNotificationsQueryKey, 0)
    },
  })

  const markAsReadMutation = useMutation({
    mutationFn: (notificationId: string) => notificationService.markAsRead(notificationId),
    onSuccess: (result, notificationId) => {
      if (!result.success) return

      let unreadDelta = 0
      queryClient.setQueryData<Notification[]>(notificationsQueryKey, (current = []) =>
        current.map((item) => {
          if (item.id !== notificationId || item.read) return item
          unreadDelta -= 1
          return { ...item, read: true }
        })
      )

      if (unreadDelta !== 0) {
        queryClient.setQueryData<number>(unreadNotificationsQueryKey, (current = 0) => Math.max(0, current + unreadDelta))
      }
    },
  })

  useEffect(() => {
    if (!patient?.id) return
    const subscription = notificationService.subscribeToPatientNotifications(patient.id, (incoming) => {
      let wasInserted = false
      queryClient.setQueryData<Notification[]>(notificationsQueryKey, (prev = []) => {
        const exists = prev.some((item) => item.id === incoming.id)
        if (exists) {
          return prev.map((item) => item.id === incoming.id ? { ...item, ...incoming, read: item.read } : item)
        }
        wasInserted = true
        return [incoming, ...prev]
      })

      if (wasInserted && !incoming.read) {
        queryClient.setQueryData<number>(unreadNotificationsQueryKey, (current = 0) => current + 1)
      }
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [notificationsQueryKey, patient?.id, queryClient, unreadNotificationsQueryKey])

  const unreadCount = useMemo(() => notifications.filter((item) => !item.read).length, [notifications])

  const handleMarkAllAsRead = async () => {
    if (!patient?.id) return
    await markAllAsReadMutation.mutateAsync()
  }

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.read) {
      await markAsReadMutation.mutateAsync(notification.id)
    }

    const target = await notificationService.resolvePatientNotificationRoute(notification)
    if (target) navigate(target)
  }

  if (isLoading) {
    return (
      <div className="p-4 sm:p-6 space-y-4 animate-pulse">
        <div className="h-8 w-40 bg-muted" />
        {[...Array(5)].map((_, i) => (
          <div key={i} className="h-24 bg-muted" />
        ))}
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 space-y-5">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <button title="Back" onClick={() => navigate(-1)} className="p-2 hover:bg-muted rounded-lg transition-colors">
            <ArrowLeft size={18} />
          </button>
          <div className="min-w-0">
            <h1 className="text-2xl font-bold text-foreground">Notifications</h1>
            {unreadCount > 0 && <p className="text-sm text-muted-foreground mt-0.5">{unreadCount} unread</p>}
          </div>
        </div>
        {unreadCount > 0 && (
          <button onClick={handleMarkAllAsRead} className="text-sm text-primary hover:underline">
            Mark all read
          </button>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="bg-card border border-dashed border-border p-10 text-center">
          <Bell size={40} className="mx-auto text-muted-foreground/40 mb-3" />
          <p className="text-sm font-medium text-foreground">No notifications</p>
          <p className="text-xs text-muted-foreground mt-1">You are all caught up.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {notifications.map((notification) => (
            <button
              key={notification.id}
              onClick={() => handleNotificationClick(notification)}
              className={clsx(
                'w-full text-left bg-card border border-border p-4 transition-colors hover:bg-muted/30',
                !notification.read && 'border-l-4 border-l-primary bg-primary/5'
              )}
            >
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-muted flex items-center justify-center shrink-0">
                  <NotificationIcon type={notification.type} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-semibold text-foreground">{notification.title}</p>
                    <span className="text-xs text-muted-foreground shrink-0">{formatTime(notification.created_at)}</span>
                  </div>
                  <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{notification.message}</p>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  )
}